import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { lt } from '@mews/framework';
import { Banner, Icon, IconName, Stack, Tab, Tabs, Typography } from '@optimus-web/core';
import { Select } from '@mews-commander/ui';
import { useRepositoryQuery } from '@mews-commander/data-access';
import { getCurrentServiceFieldName, ServicesFieldNames } from '../../formEnums';
import { useOptionTabs, Option } from '../../../../hooks/useOptionTabs';
import { useEnterprisesProductContext } from '../../../../context/EnterprisesProductContext';
import { ServicesPayments } from './ServicesPayments';
import { ServicesPricingSingleProperty } from './ServicesPricingSingleProperty';
import { ServicesPricingMultipleProperties } from './ServicesPricingMultipleProperties';
import { isChargingConsumptionValid } from './paymentsCharging';
import { ServicesAvailability } from './ServicesAvailability';

export const ServicesForm = () => {
    const {
        state: { enterprises, selectedEnterpriseIds },
    } = useEnterprisesProductContext();

    const { data, isFetching } = useRepositoryQuery('portfolioServices', 'getIntersectedKeys', {
        enterpriseIds: selectedEnterpriseIds,
    });

    const { watch, setValue } = useFormContext();

    const currentServicesFieldValues = watch(ServicesFieldNames.Services);

    const { serviceKeys } = data || {};
    const noOptions = serviceKeys?.length === 0;

    const servicesList = useMemo(
        () => serviceKeys?.map(({ key }) => ({ label: key, value: key })) as Option[],
        [serviceKeys]
    );

    const { activeTab, selectedOptions, setActiveTab, setSelectedOptions, toggleSelection } = useOptionTabs({
        optionsList: servicesList,
        fieldPathKey: ServicesFieldNames.Services,
    });

    useEffect(() => {
        const currentServices = Object.keys(currentServicesFieldValues || {});
        if (currentServices.length > 0 && servicesList) {
            setActiveTab(currentServices[0]);
            setSelectedOptions(servicesList?.filter(({ value }) => currentServices.includes(value)));
        }
    }, [currentServicesFieldValues, servicesList, setActiveTab, setSelectedOptions]);

    useEffect(() => {
        if (selectedOptions?.length > 0) {
            selectedOptions.forEach(({ value }) => {
                const match = serviceKeys?.find(({ key }) => key === value);
                if (match) {
                    const { serviceType, bookablePeriod } = match;
                    setValue(`${getCurrentServiceFieldName(value)}.${ServicesFieldNames.ServiceType}`, serviceType);
                    setValue(
                        `${getCurrentServiceFieldName(value)}.${ServicesFieldNames.BookablePeriod}`,
                        bookablePeriod
                    );
                }
            });
        }
    }, [serviceKeys, selectedOptions, setValue]);

    return (
        <Stack spacing="300" vertical>
            <Stack spacing="200" vertical>
                <Typography textStyle="titleSmallStrong" as="div">
                    {lt('ConfigureProductServicesSubHeading')}
                </Typography>
                <Typography textStyle="bodyMedium" variant="secondary" as="div">
                    {lt('ConfigureProductServicesBody')}
                </Typography>
                {noOptions && (
                    <Banner
                        variant="info"
                        primaryText={lt('ConfigureProductServicesInfoBannerPrimaryText')}
                        secondaryText={lt('ConfigureProductServicesInfoBannerSecondaryText')}
                    />
                )}
            </Stack>
            <Select
                data-test-field={ServicesFieldNames.Services}
                label={lt('ConfigureProductDropdownServices')}
                options={servicesList ?? []}
                selected={selectedOptions?.map(({ value }) => value)}
                onChange={toggleSelection}
                multiselect
                disabled={noOptions}
            />
            {selectedOptions.length > 0 && (
                <Stack spacing="200" vertical>
                    <Typography textStyle="titleSmallStrong" as="div">
                        {lt('ConfigureProductSubHeadingConfigurations')}
                    </Typography>
                    <Typography textStyle="bodyMedium" variant="secondary" as="div">
                        {lt('ConfigureProductBodyConfigurations')}
                    </Typography>
                </Stack>
            )}
            <Tabs
                activeTabValue={activeTab}
                inline
                onTabClick={(e, value) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveTab(value);
                }}
                showDivider
            >
                {(selectedOptions || []).map(({ value, label }) => (
                    <Tab
                        id={value}
                        key={value}
                        label={
                            <Stack spacing="100" itemAlignment="center">
                                {isChargingConsumptionValid(currentServicesFieldValues[value]) ? (
                                    <Icon size="small" variant="success" value={IconName.CheckCircle} />
                                ) : (
                                    <Icon size="small" variant="danger" value={IconName.AlertCircle} />
                                )}
                                {label}
                            </Stack>
                        }
                        value={value}
                    />
                ))}
            </Tabs>
            {(selectedOptions || []).map(
                ({ value }) =>
                    activeTab === value && (
                        <Stack key={value} vertical spacing="300">
                            {enterprises.length > 1 && (
                                <Banner primaryText={lt('ConfigureProductServicesInfoBannerPayment')} variant="info" />
                            )}
                            <ServicesPayments selectedService={value} isFetching={isFetching} />

                            <ServicesAvailability currentService={value} />

                            {enterprises.length > 1 ? (
                                <ServicesPricingMultipleProperties currentService={value} />
                            ) : (
                                <ServicesPricingSingleProperty currentService={value} />
                            )}
                        </Stack>
                    )
            )}
        </Stack>
    );
};
